import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import ModalSkeleton from "components/Modals/ModalSkeleton";
import CustomButton from "components/common/Button/CustomButton";
import SelectOption from "components/common/Select/Select";
import PermissionTable from "../PermissionsTable";
import InputField from "components/common/Input/Input";
import { REGEX_ALPHABETS_ONLY, portalTypeOptions } from "Constants/constant";
import _ from "lodash";
import {
  collectRolePermissionIds,
  isAiPermissionsParent,
  isShippedAiPermission,
  normalizePermissionsList,
} from "config/aiRolePermissions";

const RoleAndPermissionModal = ({
  isOpen,
  setIsOpenModal,
  selectedPermissionUUID = null,
  roleAndPermisisonCreateOrUpdateRequest,
  roleAndPermissionsCreateOrUpdateLoader,
  defaultRoleAndPermissions,
  defaultRoleAndPerLoader,
  defaultRolesAndPermissionsRequest,
  resetDefaultRolesPermissionLocally,
  selectedPortalFilter,
  isUserGracePeriodOver,
}) => {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
    clearErrors,
    watch,
  } = useForm();

  const watchFormData = watch();

  const [rolesPermissions, setRolesPermissions] = useState([]);
  const [expandedRows, setExpandedRows] = useState([]);

  const [createLoader, setCreateLoader] = useState(false);

  useEffect(() => {
    const portalType =
      watchFormData?.portal_type ||
      defaultRoleAndPermissions?.role?.type ||
      selectedPortalFilter?.value;
    const permissions = defaultRoleAndPermissions?.permissions
      ? normalizePermissionsList(
          defaultRoleAndPermissions.permissions
            .filter((x) => {
              // Integrations (connect Wyzed etc.) is Primary Portal only.
              if (
                portalType === portalTypeOptions[1].value &&
                x?.name === "integrations"
              ) {
                return false;
              }
              return true;
            })
            .map((x) => {
              const child = x?.children || [];
              const children = child.map((y) => ({
                ...y,
                isChecked: y.status,
              }));
              const anyChildChecked = children.some((y) => y.isChecked);

              return {
                ...x,
                isChecked: children.length > 0 ? anyChildChecked : x.status,
                children,
              };
            })
        )
      : [];

    setRolesPermissions(permissions);

    if (defaultRoleAndPermissions?.role && selectedPermissionUUID) {
      setValue("portal_type", defaultRoleAndPermissions.role.type);
      setValue("role_title", defaultRoleAndPermissions.role.name);
    }
  }, [
    defaultRoleAndPermissions,
    selectedPermissionUUID,
    watchFormData?.portal_type,
    selectedPortalFilter?.value,
  ]);

  useEffect(() => {
    if (selectedPermissionUUID !== null) {
      defaultRolesAndPermissionsRequest({ id: selectedPermissionUUID });
    }
    if (
      selectedPermissionUUID === null &&
      selectedPortalFilter &&
      selectedPortalFilter?.value
    ) {
      setValue("portal_type", selectedPortalFilter.value);
    }

    return () => resetDefaultRolesPermissionLocally();
  }, []);

  const modalFooter = () => (
    <CustomButton
      type='submit'
      label={selectedPermissionUUID ? "Update" : "Create"}
      showLoading={roleAndPermissionsCreateOrUpdateLoader}
      isDisabled={
        roleAndPermissionsCreateOrUpdateLoader ||
        defaultRoleAndPerLoader ||
        isUserGracePeriodOver
      }
      clickHandler={() => {
        if (isUserGracePeriodOver) {
          return;
        }
        const buttonRef = document.getElementById("addRole");
        if (buttonRef) {
          buttonRef.click();
        }
      }}
    />
  );

  const closeModal = () => {
    setIsOpenModal(false);
    reset();
  };

  useEffect(() => {
    if (createLoader && !roleAndPermissionsCreateOrUpdateLoader) {
      closeModal();
      setCreateLoader(false);
    }
  }, [roleAndPermissionsCreateOrUpdateLoader]);

  const onSubmit = (data) => {
    if (isUserGracePeriodOver) {
      return;
    }
    const permissions = collectRolePermissionIds(rolesPermissions);
    const payload = {
      permissions,
      name: data.role_title,
      type: data.portal_type,
    };
    if (selectedPermissionUUID !== null) {
      Object.assign(payload, {
        id: selectedPermissionUUID,
      });
    }
    setCreateLoader(true);
    roleAndPermisisonCreateOrUpdateRequest(payload);
  };

  useEffect(() => {
    if (watchFormData?.portal_type && selectedPermissionUUID === null) {
      const payload = { type: watchFormData?.portal_type };
      defaultRolesAndPermissionsRequest(payload);
    }
  }, [watchFormData?.portal_type]);

  const handleRowClick = (id) => {
    const newExpandedRows = [...expandedRows];
    if (expandedRows.includes(id)) {
      const indexToRemove = newExpandedRows.indexOf(id);
      newExpandedRows.splice(indexToRemove, 1);
    } else {
      newExpandedRows.push(id);
    }
    setExpandedRows(newExpandedRows);
  };

  const handleSwitchChange = (parentId, value, childId = null) => {
    const obj = _.cloneDeep(rolesPermissions);
    const parentIndex = obj.findIndex(
      (entry) => entry?.id === parentId && entry?.parent_id === null
    );

    if (parentIndex === -1) {
      return;
    }

    if (childId === null) {
      Object.assign(obj[parentIndex], {
        isChecked: value,
        children: (obj[parentIndex].children || []).map((child) => {
          if (
            isAiPermissionsParent(obj[parentIndex]) &&
            !isShippedAiPermission(child.name)
          ) {
            return child;
          }
          return { ...child, isChecked: value };
        }),
      });
    } else {
      const childIndex = obj[parentIndex].children?.findIndex(
        (entry) => entry?.id === childId
      );

      if (childIndex === -1 || childIndex === undefined) {
        return;
      }

      const child = obj[parentIndex].children[childIndex];
      if (
        isAiPermissionsParent(obj[parentIndex]) &&
        !isShippedAiPermission(child.name)
      ) {
        return;
      }
      obj[parentIndex].children.splice(childIndex, 1, {
        ...child,
        isChecked: value,
      });
      const isSome = obj[parentIndex].children.some(
        (x) =>
          (!isAiPermissionsParent(obj[parentIndex]) ||
            isShippedAiPermission(x.name)) &&
          x.isChecked === true
      );
      Object.assign(obj[parentIndex], {
        isChecked: isSome,
      });
    }

    setRolesPermissions([...obj]);
  };

  return (
    <ModalSkeleton
      isOpen={isOpen}
      cssClass='w-[60%]'
      title={`${selectedPermissionUUID ? "Edit" : "Add"} Roles & Permissions`}
      closeModal={closeModal}
      modalFooter={modalFooter()}
    >
      <div>
        <form onSubmit={handleSubmit(onSubmit)}>
          {!defaultRoleAndPerLoader && (
            <div className='grid grid-cols-2 gap-x-4'>
              <div className='col-span-2 sm:col-span-1'>
                <label className='block text-sm font-medium text-gray-700'>
                  Portal Type
                </label>
                <SelectOption
                  name='portal_type'
                  isRequired={true}
                  register={register}
                  setValue={setValue}
                  clearErrors={clearErrors}
                  data={portalTypeOptions}
                  placeHolder='Select Portal'
                  selectedOption={portalTypeOptions.find(
                    (p) => p.value === watchFormData?.portal_type
                  )}
                  isSearch={false}
                  cssClass='z-20 mt-1'
                  errorMessage={
                    errors?.portal_type?.type === "required"
                      ? "This field is required!"
                      : null
                  }
                  isDisabled={defaultRoleAndPerLoader || selectedPermissionUUID}
                />
              </div>
              <div className='col-span-2 sm:col-span-1'>
                <InputField
                  pattern={REGEX_ALPHABETS_ONLY}
                  setValue={setValue}
                  register={register}
                  label={"Role Title"}
                  type='text'
                  name='role_title'
                  isRequired={true}
                  ErrorMessage={
                    errors?.role_title?.type === "required"
                      ? "This field is required!"
                      : errors?.role_title?.type === "pattern"
                      ? "Only alphabets and numbers allow!"
                      : null
                  }
                  value={watchFormData?.role_title}
                  isDisabled={defaultRoleAndPerLoader}
                />
              </div>
            </div>
          )}
          <div className='mt-4'>
            <PermissionTable
              isLoading={defaultRoleAndPerLoader}
              rolesPermissions={rolesPermissions}
              handleRowClick={handleRowClick}
              expandedRows={expandedRows}
              handleSwitchChange={handleSwitchChange}
              portalType={watchFormData?.portal_type ?? null}
            />
          </div>
          <button type='submit' className='hidden sr-only' id='addRole' />
        </form>
      </div>
    </ModalSkeleton>
  );
};

export default RoleAndPermissionModal;
